import { useState } from 'react';
import { Search } from 'lucide-react';
import { FloatingOverlay } from '@/components/ui/floating-overlay';
import { type ChatChannelMemberResponse } from '@/lib/api';

interface MentionOverlayProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  members: ChatChannelMemberResponse[];
  onSelect: (member: ChatChannelMemberResponse) => void;
}

export function MentionOverlay({
  open,
  onClose,
  anchorRef,
  members,
  onSelect,
}: MentionOverlayProps) {
  const [search, setSearch] = useState('');

  const filtered = members.filter((m) => {
    const name = (m.displayName || m.nickname || m.userId).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const handleSelect = (member: ChatChannelMemberResponse) => {
    onSelect(member);
    setSearch('');
    onClose();
  };

  return (
    <FloatingOverlay
      open={open}
      onClose={() => {
        setSearch('');
        onClose();
      }}
      anchorRef={anchorRef}
      preferredPlacement="top"
      className="w-64 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
    >
      <div className="p-2 border-b border-border/60">
        <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/40 rounded-lg">
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members…"
            className="flex-1 bg-transparent text-sm pointer-coarse:text-base outline-none placeholder:text-muted-foreground"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearch('');
                onClose();
                e.stopPropagation();
              }
            }}
          />
        </div>
      </div>

      <div className="max-h-52 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No members found</p>
        ) : (
          filtered.map((m) => {
            const displayName = m.displayName || m.nickname || m.userId;
            return (
              <button
                key={m.userId}
                type="button"
                onClick={() => handleSelect(m)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted/60 transition-colors"
              >
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-primary">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  {m.role !== 'member' && (
                    <p className="text-[10px] text-muted-foreground capitalize">{m.role}</p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </FloatingOverlay>
  );
}
